import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import {
  snapshotPushSubscription,
  samePushSubscription,
  pushActionId,
  pushSetupChanged,
  registerWebPushAttempt,
} from '../../services/webPush';

const CHANGED_EVENT = 'jg:webpush-changed';
const FOREGROUND_REFRESH_MS = 60000;
const permissionNow = () =>
  typeof Notification === 'undefined' ? 'unavailable' : Notification.permission;

export const getWebPushSupportSnapshot = () => {
  const browser = typeof window !== 'undefined';
  const nav = browser ? window.navigator : {};
  const ios =
    /iPad|iPhone|iPod/i.test(nav.userAgent || '') ||
    (/Macintosh/i.test(nav.userAgent || '') && nav.maxTouchPoints > 1);
  const standalone =
    browser &&
    (nav.standalone === true ||
      window.matchMedia?.('(display-mode: standalone)')?.matches);
  const supported = !!(
    browser &&
    window.isSecureContext &&
    'serviceWorker' in nav &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  );
  const installRequired = !!(
    browser &&
    window.isSecureContext &&
    ios &&
    !standalone &&
    !supported
  );
  return {
    supported,
    installRequired,
    permission: permissionNow(),
    hydrated: false,
    ready: false,
    hasSubscription: false,
    registered: false,
    preferenceEnabled: null,
    systemEnabled: null,
    subscribed: false,
    testAvailable: false,
    busy: false,
    phase: 'checking',
    reason: '',
  };
};

export async function getPushRegistration(timeoutMs = 5000) {
  if (!('serviceWorker' in navigator)) return null;
  let timer;
  try {
    return await Promise.race([
      (async () =>
        (await navigator.serviceWorker.getRegistration()) ||
        (await navigator.serviceWorker.ready))(),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function keyBytes(value) {
  const raw = atob(
    String(value || '')
      .replace(/-/g, '+')
      .replace(/_/g, '/'),
  );
  const bytes = Uint8Array.from(raw, (c) => c.charCodeAt(0));
  if (bytes.length !== 65 || bytes[0] !== 4)
    throw new Error('invalid_push_key');
  return bytes;
}

const headersFor = (jwt) => ({ headers: { Authorization: `Bearer ${jwt}` } });
const unavailable = 'Alerts are temporarily unavailable. Try again later.';

// One current-account/request owner for permission, device and server state.
// Background checks are read-only: discovering a subscription never opts in.
export default function useWebPushStatus({
  enabled = true,
  jwt: suppliedJwt,
  onStateChange,
  onSubscribed,
} = {}) {
  const auth = useContext(AuthContext);
  const jwt = suppliedJwt === undefined ? auth?.player?.token : suppliedJwt;
  const owner = useRef({
    jwt,
    generation: 0,
    mounted: true,
    busy: false,
    lastRefresh: 0,
  });
  if (owner.current.jwt !== jwt) {
    owner.current = {
      jwt,
      generation: owner.current.generation + 1,
      mounted: true,
      busy: false,
      lastRefresh: 0,
    };
  }
  const callbacks = useRef({});
  callbacks.current = { onStateChange, onSubscribed };
  const flight = useRef(null);
  const observation = useRef(null);
  const [stored, setStored] = useState(() => ({
    ...getWebPushSupportSnapshot(),
    jwt,
  }));
  const isCurrent = useCallback(
    (generation) =>
      owner.current.mounted &&
      owner.current.jwt === jwt &&
      owner.current.generation === generation,
    [jwt],
  );
  const publish = useCallback(
    (value, generation) => {
      if (!isCurrent(generation)) return null;
      setStored({ ...value, jwt });
      callbacks.current.onStateChange?.(value);
      return value;
    },
    [isCurrent, jwt],
  );

  const inspect = useCallback(
    async (generation) => {
      const next = { ...getWebPushSupportSnapshot(), hydrated: true };
      if (!enabled || !jwt) return { ...next, phase: 'signin' };
      if (next.installRequired) return { ...next, phase: 'install' };
      if (!next.supported) return { ...next, phase: 'unsupported' };
      if (next.permission === 'denied') return { ...next, phase: 'denied' };
      const reg = await getPushRegistration();
      if (!isCurrent(generation)) return null;
      if (!reg)
        return {
          ...next,
          phase: 'worker',
          reason: 'Alerts are still setting up. Try again.',
        };
      next.ready = true;
      const sub = await reg.pushManager.getSubscription();
      if (!isCurrent(generation)) return null;
      next.hasSubscription = !!sub;
      if (!sub) {
        observation.current = { jwt, subscription: null };
        return { ...next, phase: 'enable' };
      }
      const snapshot = snapshotPushSubscription(sub);
      try {
        const response = await axiosConn.post(
          'notifications/webpush-status',
          snapshot,
          headersFor(jwt),
        );
        if (!isCurrent(generation)) return null;
        const current = await reg.pushManager.getSubscription();
        if (!isCurrent(generation)) return null;
        if (!samePushSubscription(current, snapshot)) {
          observation.current = null;
          return {
            ...next,
            hasSubscription: !!current,
            phase: current ? 'reconnect' : 'enable',
          };
        }
        const server = response?.data?.data;
        if (
          response?.data?.success !== true ||
          !server ||
          ['registered', 'preferenceEnabled', 'systemEnabled'].some(
            (key) => typeof server[key] !== 'boolean',
          )
        ) {
          throw new Error('invalid_push_status');
        }
        Object.assign(next, {
          registered:
            server.registered && typeof server.claimObservation === 'string',
          preferenceEnabled: server.preferenceEnabled,
          systemEnabled: server.systemEnabled,
          testAvailable: server.testAvailable === true,
          permission: permissionNow(),
        });
        observation.current =
          typeof server.claimObservation === 'string' &&
          Number.isFinite(server.observationExpiresAt)
            ? {
                jwt,
                subscription: snapshot,
                proof: server.claimObservation,
                expiresAt: server.observationExpiresAt,
              }
            : null;
        next.subscribed =
          next.permission === 'granted' &&
          next.registered &&
          next.preferenceEnabled &&
          next.systemEnabled;
        next.phase = !next.systemEnabled
          ? 'unavailable'
          : next.permission === 'denied'
          ? 'denied'
          : next.permission !== 'granted'
          ? 'enable'
          : !next.preferenceEnabled
          ? 'off'
          : next.subscribed
          ? 'enabled'
          : 'reconnect';
        return next;
      } catch {
        if (isCurrent(generation)) observation.current = null;
        return {
          ...next,
          phase: 'reconnect',
          reason:
            "We couldn't confirm alerts on this device. Try reconnecting.",
        };
      }
    },
    [enabled, isCurrent, jwt],
  );

  const refresh = useCallback(
    ({ force = false } = {}) => {
      if (owner.current.busy) return Promise.resolve(null);
      if (!force && flight.current?.jwt === jwt) return flight.current.promise;
      const generation = ++owner.current.generation;
      observation.current = null;
      owner.current.lastRefresh = Date.now();
      const task = (async () => {
        publish(
          { ...getWebPushSupportSnapshot(), phase: 'checking' },
          generation,
        );
        try {
          const next = await inspect(generation);
          return next && publish(next, generation);
        } catch {
          return publish(
            {
              ...getWebPushSupportSnapshot(),
              hydrated: true,
              phase: 'reconnect',
              reason: "We couldn't check alerts. Try again.",
            },
            generation,
          );
        }
      })();
      flight.current = { jwt, promise: task };
      task.finally(() => {
        if (flight.current?.promise === task) flight.current = null;
      });
      return task;
    },
    [inspect, jwt, publish],
  );

  useEffect(() => {
    owner.current.mounted = true;
    refresh({ force: true });
    const foreground = () => {
      if (
        document.visibilityState === 'visible' &&
        Date.now() - owner.current.lastRefresh >= FOREGROUND_REFRESH_MS
      )
        refresh();
    };
    const changed = () => refresh({ force: true });
    document.addEventListener('visibilitychange', foreground);
    window.addEventListener('focus', foreground);
    window.addEventListener(CHANGED_EVENT, changed);
    return () => {
      owner.current.mounted = false;
      owner.current.generation += 1;
      document.removeEventListener('visibilitychange', foreground);
      window.removeEventListener('focus', foreground);
      window.removeEventListener(CHANGED_EVENT, changed);
    };
  }, [refresh]);

  const act = async (kind, vapidKey) => {
    if (owner.current.busy || !enabled || !jwt) return;
    // Capture before ANY asynchronous work. This action can never adopt a later
    // observation response, even if that response arrived before its INIT.
    const captured =
      observation.current?.jwt === jwt ? observation.current : null;
    const wantsOptIn =
      kind === 'enable' && ['enable', 'off'].includes(stored.phase);
    const generation = ++owner.current.generation;
    owner.current.busy = true;
    flight.current = null;
    const base = { ...getWebPushSupportSnapshot(), hydrated: true, busy: true };
    publish(
      { ...base, phase: kind === 'enable' ? 'connecting' : 'disconnecting' },
      generation,
    );
    let subscriptionExists = stored.jwt === jwt && stored.hasSubscription;
    try {
      const actionId = kind === 'enable' ? pushActionId() : null;
      if (!base.supported || base.installRequired)
        throw new Error('push_unavailable');
      if (kind === 'enable') {
        const bytes = keyBytes(vapidKey);
        let permission = permissionNow();
        // Invoked synchronously from the button, before unrelated awaits.
        if (permission === 'default')
          permission = await Notification.requestPermission();
        if (!isCurrent(generation)) return;
        if (permission !== 'granted') {
          publish(
            {
              ...base,
              busy: false,
              permission,
              phase: permission === 'denied' ? 'denied' : 'enable',
            },
            generation,
          );
          return;
        }
        const reg = await getPushRegistration();
        if (!isCurrent(generation)) return;
        if (!reg) throw new Error('push_unavailable');
        let sub = await reg.pushManager.getSubscription();
        if (!isCurrent(generation)) return;
        let bootstrap = false;
        if (sub) {
          if (
            !captured?.proof ||
            captured.expiresAt <= Date.now() ||
            !samePushSubscription(sub, captured.subscription)
          )
            throw pushSetupChanged();
        } else {
          if (captured?.subscription) throw pushSetupChanged();
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: bytes,
          });
          bootstrap = true;
        }
        if (!isCurrent(generation)) return;
        subscriptionExists = true;
        const current = await reg.pushManager.getSubscription();
        if (!isCurrent(generation)) return;
        const snapshot = snapshotPushSubscription(sub);
        if (!samePushSubscription(current, snapshot)) throw pushSetupChanged();
        const completed = await registerWebPushAttempt({
          jwt,
          subscription: snapshot,
          observationProof: captured?.proof,
          bootstrap,
          actionId,
          wantsOptIn,
          isCurrent: async () => {
            if (!isCurrent(generation)) return false;
            const local = await reg.pushManager.getSubscription();
            if (!isCurrent(generation)) return false;
            if (!samePushSubscription(local, snapshot))
              throw pushSetupChanged();
            return true;
          },
        });
        if (!completed || !isCurrent(generation)) return;
        const next = await inspect(generation);
        if (next) publish(next, generation);
        if (next?.subscribed && isCurrent(generation))
          callbacks.current.onSubscribed?.();
      } else {
        const reg = await getPushRegistration();
        if (!isCurrent(generation)) return;
        if (!reg) throw new Error('push_unavailable');
        const sub = await reg.pushManager.getSubscription();
        if (!isCurrent(generation)) return;
        if (sub) {
          if (
            !captured?.proof ||
            captured.expiresAt <= Date.now() ||
            !samePushSubscription(sub, captured.subscription)
          )
            throw pushSetupChanged();
          const response = await axiosConn.delete(
            'notifications/register-webpush',
            {
              ...headersFor(jwt),
              data: {
                ...captured.subscription,
                observationProof: captured.proof,
              },
            },
          );
          if (!isCurrent(generation)) return;
          if (response?.data?.success !== true)
            throw new Error('unregister_failed');
          if (!response.data.data?.removed) throw pushSetupChanged();
          const current = await reg.pushManager.getSubscription();
          if (!isCurrent(generation)) return;
          if (!samePushSubscription(current, captured.subscription))
            throw pushSetupChanged();
          // The server tombstone turns this device off. Unsubscribing the shared
          // browser endpoint here could invalidate a newer account/tab's setup.
          await inspect(generation);
          if (!isCurrent(generation)) return;
        }
        publish(
          {
            ...base,
            busy: false,
            ready: true,
            phase: 'enable',
            reason: 'Alerts turned off on this device.',
          },
          generation,
        );
      }
      if (isCurrent(generation)) window.dispatchEvent(new Event(CHANGED_EVENT));
    } catch (err) {
      const code = err?.response?.data?.code || err?.code;
      if (
        [
          'CLAIM_SUPERSEDED',
          'CLAIM_PENDING',
          'INVALID_PROOF',
          'PUSH_STATUS_REQUIRED',
          'PUSH_SETUP_UPDATE_REQUIRED',
        ].includes(code)
      ) {
        // Read-only recovery is allowed; replaying this gesture is not.
        let next;
        try {
          next = await inspect(generation);
        } catch {
          next = {
            ...base,
            busy: false,
            phase: 'reconnect',
            hasSubscription: subscriptionExists,
          };
        }
        if (next)
          publish(
            {
              ...next,
              busy: false,
              reason: next.subscribed
                ? ''
                : code === 'PUSH_SETUP_UPDATE_REQUIRED'
                ? 'Refresh Joe Galaxy before changing alerts on this device.'
                : 'Alerts changed on this device. Check their status before reconnecting.',
            },
            generation,
          );
        return;
      }
      // Never display/log provider errors: they may contain subscription secrets.
      publish(
        {
          ...base,
          permission: permissionNow(),
          busy: false,
          hasSubscription: subscriptionExists,
          phase:
            kind === 'disable'
              ? 'disable-error'
              : permissionNow() === 'denied'
              ? 'denied'
              : subscriptionExists
              ? 'reconnect'
              : 'enable',
          reason:
            kind === 'enable'
              ? "We couldn't connect alerts. Try again."
              : "We couldn't finish turning alerts off. Try again.",
        },
        generation,
      );
    } finally {
      if (isCurrent(generation)) owner.current.busy = false;
    }
  };

  const state = stored.jwt === jwt ? stored : getWebPushSupportSnapshot();
  return {
    ...state,
    refresh,
    enablePush: (key) => act('enable', key),
    disablePush: () => act('disable'),
    unavailable,
  };
}
